import { useState } from "react";
import Placeholder from "../../assets/CoursePlaceholder.png";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getBlogs } from "../../api/Blogs/GetAllBlogs";
import Spinner from "../Shared/Spinner";

const BlogsSection = () => {
    const { data: BlogsResponse, isLoading: isBlogsLoading } = useQuery({
        queryFn: getBlogs,
        queryKey: ["blogs"],
        staleTime: 0,
        refetchInterval: 0,
    });

    const blogs = BlogsResponse && BlogsResponse.data && Array.isArray(BlogsResponse.data.blogsData) 
    ? BlogsResponse.data.blogsData 
    : [];
    const [currentPage, setCurrentPage] = useState(1);
    const blogsPerPage = 6;

    // Calculate total pages
    const totalPages = Math.ceil(blogs.length / blogsPerPage);

    // Determine the blogs to show based on the current page
    const indexOfFirstBlog = (currentPage - 1) * blogsPerPage;
    const indexOfLastBlog = indexOfFirstBlog + blogsPerPage;
    const currentBlogs = blogs.slice(indexOfFirstBlog, indexOfLastBlog);

    const truncateText = (text: string, wordLimit: number): string => {
        return text.split(" ").slice(0, wordLimit).join(" ") + (text.split(" ").length > wordLimit ? "..." : "");
    };

    // Pagination navigation
    const handlePrevious = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    if (isBlogsLoading)
        return (
            <div className="min-h-[80vh] flex items-center justify-center">
                <Spinner />
            </div>
        );

    return (
        <div className="mt-16 pt-1.5 container">
            {currentBlogs.length > 0 && (
                <div className="w-full border border-textPrimary/10 rounded-md mb-16 lg:flex hidden">
                    <div className="w-[57%] border-r border-textPrimary/10">
                        <img
                            className="w-full object-cover h-full"
                            src={currentBlogs[0]?.img_url || Placeholder}
                            alt={currentBlogs[0]?.title || ""}
                        />
                    </div>
                    <div className="xl:px-10 px-8 xl:py-12 py-6 xl:w-[43%] w-1/2">
                        <div className="flex gap-4 mb-6 items-center">
                            <span className="bg-textPrimary px-2 py-1 text-sm font-medium text-white">
                                Dr. Mohamed Ismael
                            </span>
                            <p className="text-textPrimary font-medium text-sm">5 min read</p>
                        </div>
                        <h1 className="mb-4 text-textPrimary font-bold text-4xl">
                            {currentBlogs[0]?.title || ""}
                        </h1>
                        <p className="text-textPrimary mb-20">
                            {truncateText(currentBlogs[0]?.brief || "", 25)}
                        </p>
                        <Link
                            className="flex gap-1 items-center text-textPrimary rounded border border-textPrimary w-fit py-2 px-4 hover:text-white hover:bg-textPrimary transition-all duration-300"
                            to={`/blog/${currentBlogs[0]?.id || ""}`}
                        >
                            Read More <ChevronRight size={16} />
                        </Link>
                    </div>
                </div>
            )}
            <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 lg:gap-x-8 gap-x-4 lg:gap-y-16 gap-y-8 sm:pb-20 pb-14">
                {currentBlogs.map((blog, index) => (
                    <div className={`w-full ${index === 0 ? "lg:hidden block" : ""}`} key={blog.id}>
                        <div className="sm:h-[300px] h-[250px]">
                            <img
                                className="w-full object-cover h-full"
                                src={blog.img_url || Placeholder}
                                alt={blog.title}
                            />
                        </div>
                        <div className="sm:p-6 p-4 border border-textPrimary/10 rounded-b-md sm:h-[calc(100%_-300px)] h-[calc(100%_-250px)]">
                            <div className="flex gap-4 mb-4 items-center">
                                <span className="bg-textPrimary px-2 py-1 text-sm font-medium text-white">
                                    Dr. Mohamed Ismael
                                </span>
                                <p className="text-textPrimary font-medium text-sm">5 min read</p>
                            </div>
                            <h1 className="mb-2 text-textPrimary font-bold lg:text-2xl text-lg sm:text-xl">
                                {blog.title}
                            </h1>
                            <p className="text-textPrimary mb-6 sm:text-base text-sm">
                                {truncateText(blog.brief, 25)}
                            </p>
                            <Link
                                className="flex gap-1 items-center text-textPrimary rounded border border-textPrimary w-fit sm:py-2 py-1.5 sm:px-4 px-2.5 hover:text-white hover:bg-textPrimary transition-all duration-300 sm:text-base text-sm"
                                to={`/blog/${blog.id}`}
                            >
                                Read More <ChevronRight size={16} />
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex gap-4 justify-center sm:pb-20 pb-16">
                <button
                    className="sm:size-10 size-8 sm:text-base text-sm hover:bg-secondary hover:text-white border-textPrimary/10 border rounded-sm flex items-center justify-center text-textPrimary duration-300 transition-all"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                >
                    <ChevronRight size={20} className="rotate-180" />
                </button>
                {[...Array(totalPages)].map((_, index) => (
                    <button
                        key={index}
                        className={`sm:size-10 size-8 sm:text-base text-sm border-textPrimary/10 border rounded-sm ${
                            currentPage === index + 1 ? "bg-secondary text-white" : ""
                        }`}
                        onClick={() => setCurrentPage(index + 1)}
                    >
                        {index + 1}
                    </button>
                ))}
                <button
                    className="sm:size-10 size-8 sm:text-base text-sm hover:bg-secondary hover:text-white border-textPrimary/10 border rounded-sm flex items-center justify-center text-textPrimary duration-300 transition-all"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                >
                    <ChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};

export default BlogsSection;
